import React, {useEffect, useMemo, useState} from 'react';
import {Box, Paper, styled, Typography} from '@mui/material';
import {DataGrid} from '@mui/x-data-grid';
import dayjs from 'dayjs';
import {useClient} from '../core/client';

const StyledGridOverlay = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: theme.palette.text.secondary,
}));

const NoActivityOverlay = () => (
    <StyledGridOverlay>
        <Typography variant="body2">No activity logged yet.</Typography>
    </StyledGridOverlay>
);

const columns = [
    { field: 'type', headerName: 'Type', width: 140 },
    { field: 'message', headerName: 'Message', flex: 1, minWidth: 280 },
    { field: 'entityType', headerName: 'Entity', width: 150 },
    {
        field: 'entityId',
        headerName: 'Entity ID',
        flex: 0.7,
        minWidth: 180,
        valueGetter: (params) => params.value ?? params.row?.entity?.id,
    },
    {
        field: 'userId',
        headerName: 'User ID',
        flex: 0.7,
        minWidth: 180,
        valueGetter: (params) => params.value ?? params.row?.user?.id,
    },
    {
        field: 'createdAt',
        headerName: 'Date',
        width: 200,
        valueFormatter: (params) =>
            params.value ? dayjs(params.value).format('MMM D, YYYY h:mm A') : '',
    },
];

const ActivityLog = () => {
    const client = useClient();
    const [activity, setActivity] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchAllActivity = async () => {
            setLoading(true);
            try {
                const { data } = await client.get('/activity-log');
                setActivity(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error('Failed to load activity log', error);
                setActivity([]);
            } finally {
                setLoading(false);
            }
        };

        fetchAllActivity();
    }, [client]);

    const rows = useMemo(
        () =>
            activity.map((entry) => ({
                id: entry._id || entry.id || `${entry.entityId}-${entry.createdAt}`,
                ...entry,
            })),
        [activity]
    );

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
                Activity Log
            </Typography>

            <Paper sx={{ p: 2 }}>
                <Box sx={{ width: '100%', height: 600 }}>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        pageSize={10}
                        rowsPerPageOptions={[10, 25, 50]}
                        loading={loading}
                        disableSelectionOnClick
                        slots={{
                            noRowsOverlay: NoActivityOverlay,
                        }}
                    />
                </Box>
            </Paper>
        </Box>
    );
};

export default ActivityLog;
